import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, CheckSquare, FileText, Search, X, TrendingUp, Clock, Trophy, ChevronRight, Activity, CalendarDays, PieChart, WrenchIcon } from 'lucide-react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Customer } from '../components/CustomerCard';
import { Task } from '../components/TaskCard';
import { Invoice } from '../components/InvoiceCard';
import { Earning } from '../components/EarningCard';
import { Payout } from '../components/PayoutCard';
import { StatCard } from '../components/StatCard';
import { RichTextEditor } from '../components/RichTextEditor';
interface DashboardProps {
  customers: Customer[];
  tasks: Task[];
  invoices: Invoice[];
  earnings: Earning[];
  payouts: Payout[];
  onAddTask: (task: Omit<Task, 'id' | 'customerName'>) => void;
  onEditTask: (task: Task) => void;
  onViewCustomer: (customerId: string) => void;
}
export function Dashboard({
  customers,
  tasks,
  invoices,
  earnings,
  payouts,
  onEditTask,
  onViewCustomer
}: DashboardProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<'Samlet' | 'Ladeboks' | 'Andet'>('Samlet');
  const [notes, setNotes] = useState('');
  // Load notes from Firebase with real-time sync
  useEffect(() => {
    const notesDocRef = doc(db, 'settings', 'dashboard-notes');
    const unsubscribe = onSnapshot(notesDocRef, docSnapshot => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setNotes(data.content || '');
      }
    }, error => {
      console.error('Error loading notes:', error);
    });
    return () => unsubscribe();
  }, []);
  // Save notes to Firebase with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      try {
        const notesDocRef = doc(db, 'settings', 'dashboard-notes');
        await setDoc(notesDocRef, {
          content: notes,
          updatedAt: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error saving notes:', error);
      }
    }, 1000); // Debounce: save 1 second after user stops typing
    return () => clearTimeout(timeoutId);
  }, [notes]);
  // Search Logic
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return {
      customers: [],
      tasks: []
    };
    const query = searchQuery.toLowerCase();
    const matchedCustomers = customers.filter(c => c.name.toLowerCase().includes(query) || c.customerId.toLowerCase().includes(query) || c.phone?.includes(query) || c.email?.toLowerCase().includes(query)).slice(0, 5);
    const matchedTasks = tasks.filter(t => t.title.toLowerCase().includes(query) || t.taskId?.toLowerCase().includes(query) || t.customerName?.toLowerCase().includes(query)).slice(0, 5);
    return {
      customers: matchedCustomers,
      tasks: matchedTasks
    };
  }, [searchQuery, customers, tasks]);
  // Recently active tasks (not done) - sorted by most recent installation/planning date, fallback to taskId desc
  const recentActiveTasks = useMemo(() => {
    return [...tasks].filter(t => t.status !== 'done').sort((a, b) => {
      const aDate = a.agreedInstallation || a.agreedPlanning || '';
      const bDate = b.agreedInstallation || b.agreedPlanning || '';
      if (aDate && bDate) {
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      }
      if (aDate) return -1;
      if (bDate) return 1;
      // Fallback: by taskId descending
      const aNum = parseInt(a.taskId?.substring(1) || '0');
      const bNum = parseInt(b.taskId?.substring(1) || '0');
      return bNum - aNum;
    }).slice(0, 3);
  }, [tasks]);
  // This week's appointments (Mon-Sun of current week)
  const thisWeekAppointments = useMemo(() => {
    const now = new Date();
    const day = now.getDay(); // 0 = Sun, 1 = Mon, ...
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMonday);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    const appointments: Array<{
      task: Task;
      date: Date;
      type: 'Projektering' | 'Installation';
    }> = [];
    tasks.forEach(task => {
      if (task.agreedPlanning) {
        const d = new Date(task.agreedPlanning);
        if (d >= monday && d <= sunday) {
          appointments.push({
            task,
            date: d,
            type: 'Projektering'
          });
        }
      }
      if (task.agreedInstallation) {
        const d = new Date(task.agreedInstallation);
        if (d >= monday && d <= sunday) {
          appointments.push({
            task,
            date: d,
            type: 'Installation'
          });
        }
      }
    });
    return appointments.sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [tasks]);
  // Status distribution
  const statusDistribution = useMemo(() => {
    const statuses = [{
      key: 'planned',
      label: 'Planlagt',
      color: 'bg-blue-500',
      textColor: 'text-blue-700',
      bgColor: 'bg-blue-50'
    }, {
      key: 'pending',
      label: 'Afventende',
      color: 'bg-purple-500',
      textColor: 'text-purple-700',
      bgColor: 'bg-purple-50'
    }, {
      key: 'in-progress',
      label: 'I gang',
      color: 'bg-amber-500',
      textColor: 'text-amber-700',
      bgColor: 'bg-amber-50'
    }, {
      key: 'todo',
      label: 'Todo',
      color: 'bg-stone-500',
      textColor: 'text-stone-700',
      bgColor: 'bg-stone-50'
    }, {
      key: 'done',
      label: 'Afsluttet',
      color: 'bg-emerald-500',
      textColor: 'text-emerald-700',
      bgColor: 'bg-emerald-50'
    }] as const;
    const counts = statuses.map(s => ({
      ...s,
      count: tasks.filter(t => t.status === s.key).length
    }));
    const total = tasks.length;
    return {
      total,
      items: counts.map(c => ({
        ...c,
        percentage: total > 0 ? c.count / total * 100 : 0
      }))
    };
  }, [tasks]);
  // Statistics Calculations
  const statistics = useMemo(() => {
    let filteredTasks = tasks;
    if (selectedCategory !== 'Samlet') {
      filteredTasks = tasks.filter(t => t.category === selectedCategory);
    }
    const revenue = filteredTasks.reduce((sum, t) => sum + (Number(t.totalPrice) || 0), 0);
    const hours = filteredTasks.reduce((sum, t) => sum + (Number(t.hours) || 0), 0);
    // Top 2 highest-revenue tasks
    const topTasks = [...filteredTasks].filter(t => Number(t.totalPrice) > 0).sort((a, b) => (Number(b.totalPrice) || 0) - (Number(a.totalPrice) || 0)).slice(0, 2);
    return {
      revenue,
      hours,
      topTasks
    };
  }, [tasks, selectedCategory]);
  const stats = [{
    title: 'Kunder',
    value: customers.length,
    icon: Users,
    iconBgColor: 'bg-blue-50',
    iconColor: 'text-blue-600',
    subtitle: 'Aktive kunder'
  }, {
    title: 'Opgaver',
    value: tasks.length,
    icon: CheckSquare,
    iconBgColor: 'bg-green-50',
    iconColor: 'text-green-600',
    subtitle: 'Åbne opgaver'
  }, {
    title: 'Fakturaer',
    value: invoices.length,
    icon: FileText,
    iconBgColor: 'bg-purple-50',
    iconColor: 'text-purple-600',
    subtitle: 'Antal fakturaer i alt'
  }];
  return <div className="space-y-8" data-id="element-1452">
      <header data-id="element-1453">
        <h1 className="text-3xl font-bold tracking-tight text-stone-900 mb-1" data-id="element-1454">
          Overblik
        </h1>
        <p className="text-stone-500 mb-6" data-id="element-1455">
          Komplet overblik over din virksomhed
        </p>

        {/* Search Bar and Notes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1456">
          {/* Search Card */}
          <div className="bg-white rounded-2xl border border-stone-200 p-5" data-id="element-1457">
            <div className="flex items-center gap-2 mb-4" data-id="element-1458">
              <Search className="w-4 h-4 text-stone-400" data-id="element-1459" />
              <h2 className="text-sm font-semibold text-stone-900 uppercase tracking-wide" data-id="element-1460">
                Søg
              </h2>
            </div>
            <div className="relative" data-id="element-1461">
              <div className="relative" data-id="element-1462">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-1463" />
                <input type="text" placeholder="Søg efter kunde eller sag..." value={searchQuery} onChange={e => {
                setSearchQuery(e.target.value);
                setShowSearchResults(true);
              }} onFocus={() => setShowSearchResults(true)} className="w-full pl-9 pr-10 py-2.5 rounded-lg border border-stone-200 bg-stone-50 text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-all text-sm" data-id="element-1464" />
                {searchQuery && <button onClick={() => {
                setSearchQuery('');
                setShowSearchResults(false);
              }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-stone-700 transition-colors" data-id="element-1465">
                    <X className="w-4 h-4" data-id="element-1466" />
                  </button>}
              </div>

              {/* Search Results Dropdown */}
              <AnimatePresence data-id="element-1467">
                {showSearchResults && searchQuery.trim() && <motion.div initial={{
                opacity: 0,
                y: 4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 4
              }} className="absolute top-full mt-2 w-full bg-white rounded-xl border border-stone-200 shadow-lg z-50 overflow-hidden" data-id="element-1468">
                    {searchResults.customers.length === 0 && searchResults.tasks.length === 0 ? <div className="p-4 text-center text-sm text-stone-500" data-id="element-1469">
                        Ingen resultater fundet
                      </div> : <div className="max-h-[300px] overflow-y-auto" data-id="element-1470">
                        {searchResults.customers.length > 0 && <div className="p-1.5" data-id="element-1471">
                            <h3 className="px-2.5 py-1.5 text-[10px] font-semibold text-stone-400 uppercase tracking-wider" data-id="element-1472">
                              Kunder
                            </h3>
                            {searchResults.customers.map(customer => <button key={customer.id} onClick={() => {
                      onViewCustomer(customer.id!);
                      setShowSearchResults(false);
                      setSearchQuery('');
                    }} className="w-full flex items-center gap-3 p-2 hover:bg-stone-50 rounded-lg transition-colors text-left" data-id="element-1473">
                                <Users className="w-4 h-4 text-stone-400 flex-shrink-0 ml-1" data-id="element-1474" />
                                <div data-id="element-1475">
                                  <p className="text-sm font-medium text-stone-900" data-id="element-1476">
                                    {customer.name}
                                  </p>
                                  <p className="text-xs text-stone-500" data-id="element-1477">
                                    {customer.customerId}
                                  </p>
                                </div>
                              </button>)}
                          </div>}

                        {searchResults.tasks.length > 0 && <div className="p-1.5 border-t border-stone-100" data-id="element-1478">
                            <h3 className="px-2.5 py-1.5 text-[10px] font-semibold text-stone-400 uppercase tracking-wider" data-id="element-1479">
                              Sager
                            </h3>
                            {searchResults.tasks.map(task => <button key={task.id} onClick={() => {
                      onEditTask(task);
                      setShowSearchResults(false);
                      setSearchQuery('');
                    }} className="w-full flex items-center gap-3 p-2 hover:bg-stone-50 rounded-lg transition-colors text-left" data-id="element-1480">
                                <CheckSquare className="w-4 h-4 text-stone-400 flex-shrink-0 ml-1" data-id="element-1481" />
                                <div data-id="element-1482">
                                  <p className="text-sm font-medium text-stone-900" data-id="element-1483">
                                    {task.title}
                                  </p>
                                  <p className="text-xs text-stone-500" data-id="element-1484">
                                    {task.taskId} • {task.customerName}
                                  </p>
                                </div>
                              </button>)}
                          </div>}
                      </div>}
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Notes Card */}
          <div className="bg-white rounded-2xl border border-stone-200 p-5" data-id="element-1485">
            <div className="flex items-center gap-2 mb-4" data-id="element-1486">
              <FileText className="w-4 h-4 text-stone-400" data-id="element-1487" />
              <h2 className="text-sm font-semibold text-stone-900 uppercase tracking-wide" data-id="element-1488">
                Noter
              </h2>
            </div>
            <div className="-mt-6" data-id="element-1489">
              <RichTextEditor label="" value={notes} onChange={setNotes} placeholder="Skriv dine noter her..." rows={2} data-id="element-1490" />
            </div>
          </div>
        </div>
      </header>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1491">
        {stats.map((stat, index) => <StatCard key={stat.title} index={index} title={stat.title} value={stat.value} icon={stat.icon} iconBgColor={stat.iconBgColor} iconColor={stat.iconColor} subtitle={stat.subtitle} data-id="element-1492" />)}
      </div>

      {/* Activity Row: Recent Tasks + This Week */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1493">
        {/* Senest aktive opgaver */}
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-id="element-1494">
          <div className="p-5 border-b border-stone-200 flex items-center gap-2" data-id="element-1495">
            <Activity className="w-4 h-4 text-stone-400" data-id="element-1496" />
            <h2 className="text-sm font-semibold text-stone-900 uppercase tracking-wide" data-id="element-1497">
              Senest aktive opgaver
            </h2>
          </div>
          <div className="p-2" data-id="element-1498">
            {recentActiveTasks.length === 0 ? <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-1499">
                <CheckSquare className="w-8 h-8 text-stone-200 mb-3" data-id="element-1500" />
                <p className="text-sm font-medium text-stone-900" data-id="element-1501">
                  Ingen aktive opgaver
                </p>
                <p className="text-xs text-stone-500 mt-1" data-id="element-1502">
                  Når du opretter eller arbejder på opgaver, vises de her.
                </p>
              </div> : <ul className="space-y-1" data-id="element-1503">
                {recentActiveTasks.map((task, index) => {
              const statusMap: Record<string, {
                label: string;
                color: string;
              }> = {
                'in-progress': {
                  label: 'I gang',
                  color: 'bg-amber-100 text-amber-700'
                },
                planned: {
                  label: 'Planlagt',
                  color: 'bg-blue-100 text-blue-700'
                },
                pending: {
                  label: 'Afventende',
                  color: 'bg-purple-100 text-purple-700'
                },
                todo: {
                  label: 'Todo',
                  color: 'bg-stone-100 text-stone-700'
                },
                done: {
                  label: 'Afsluttet',
                  color: 'bg-emerald-100 text-emerald-700'
                }
              };
              const statusInfo = statusMap[task.status] || statusMap.todo;
              return <motion.li key={task.id} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.05
              }} data-id="element-1504">
                      <button onClick={() => onEditTask(task)} className="group w-full flex items-center gap-3 p-3 rounded-xl hover:bg-stone-50 transition-colors text-left" data-id="element-1505">
                        <WrenchIcon className="w-4 h-4 text-stone-400 flex-shrink-0 ml-1" data-id="element-1506" />
                        <div className="flex-1 min-w-0 ml-1" data-id="element-1507">
                          <div className="flex items-center gap-2" data-id="element-1508">
                            <p className="text-sm font-medium text-stone-900 truncate" data-id="element-1509">
                              {task.title}
                            </p>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${statusInfo.color}`} data-id="element-1510">
                              {statusInfo.label}
                            </span>
                          </div>
                          <p className="text-xs text-stone-500 truncate mt-0.5" data-id="element-1511">
                            {task.customerName || 'Ingen kunde'}
                            {task.taskId && ` • ${task.taskId}`}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-teal-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" data-id="element-1512" />
                      </button>
                    </motion.li>;
            })}
              </ul>}
          </div>
        </div>

        {/* Denne uges aftaler */}
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-id="element-1513">
          <div className="p-5 border-b border-stone-200 flex items-center justify-between" data-id="element-1514">
            <div className="flex items-center gap-2" data-id="element-1515">
              <CalendarDays className="w-4 h-4 text-stone-400" data-id="element-1516" />
              <h2 className="text-sm font-semibold text-stone-900 uppercase tracking-wide" data-id="element-1517">
                Denne uges aftaler
              </h2>
            </div>
            {thisWeekAppointments.length > 0 && <span className="text-xs font-medium text-stone-500 bg-stone-100 px-2 py-0.5 rounded-full" data-id="element-1518">
                {thisWeekAppointments.length}
              </span>}
          </div>
          <div className="p-2 max-h-[280px] overflow-y-auto" data-id="element-1519">
            {thisWeekAppointments.length === 0 ? <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-1520">
                <CalendarDays className="w-8 h-8 text-stone-200 mb-3" data-id="element-1521" />
                <p className="text-sm font-medium text-stone-900" data-id="element-1522">
                  Ingen aftaler i denne uge
                </p>
                <p className="text-xs text-stone-500 mt-1" data-id="element-1523">
                  Planlagte opgaver og installationer vises her.
                </p>
              </div> : <ul className="space-y-1" data-id="element-1524">
                {thisWeekAppointments.map((appt, index) => <motion.li key={`${appt.task.id}-${appt.type}-${index}`} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.04
            }} data-id="element-1525">
                    <button onClick={() => onEditTask(appt.task)} className="group w-full flex items-center gap-3 p-3 rounded-xl hover:bg-stone-50 transition-colors text-left" data-id="element-1526">
                      <div className="flex flex-col items-center justify-center w-11 flex-shrink-0 py-1 bg-stone-50 rounded-lg border border-stone-200 ml-1" data-id="element-1527">
                        <span className="text-[9px] font-semibold text-stone-500 uppercase" data-id="element-1528">
                          {appt.date.toLocaleDateString('da-DK', {
                      weekday: 'short'
                    })}
                        </span>
                        <span className="text-sm font-bold text-stone-700 leading-none mt-0.5" data-id="element-1529">
                          {appt.date.getDate()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0 ml-1" data-id="element-1530">
                        <div className="flex items-center gap-2" data-id="element-1531">
                          <p className="text-sm font-medium text-stone-900 truncate" data-id="element-1532">
                            {appt.task.title}
                          </p>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${appt.type === 'Installation' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-1533">
                            {appt.type}
                          </span>
                        </div>
                        <p className="text-xs text-stone-500 truncate mt-0.5" data-id="element-1534">
                          {appt.date.toLocaleTimeString('da-DK', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                          {' • '}
                          {appt.task.customerName || 'Ingen kunde'}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-teal-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" data-id="element-1535" />
                    </button>
                  </motion.li>)}
              </ul>}
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-id="element-1536">
        <div className="p-5 border-b border-stone-200 flex items-center gap-2" data-id="element-1537">
          <PieChart className="w-4 h-4 text-stone-400" data-id="element-1538" />
          <h2 className="text-sm font-semibold text-stone-900 uppercase tracking-wide" data-id="element-1539">
            Status-fordeling
          </h2>
          <span className="ml-auto text-xs font-medium text-stone-500" data-id="element-1540">
            {statusDistribution.total} opgaver i alt
          </span>
        </div>
        <div className="p-6" data-id="element-1541">
          {statusDistribution.total === 0 ? <div className="p-8 flex flex-col items-center justify-center text-center" data-id="element-1542">
              <PieChart className="w-8 h-8 text-stone-200 mb-3" data-id="element-1543" />
              <p className="text-sm font-medium text-stone-900" data-id="element-1544">
                Ingen opgaver endnu
              </p>
              <p className="text-xs text-stone-500 mt-1" data-id="element-1545">
                Opret opgaver for at se fordelingen her.
              </p>
            </div> : <div className="space-y-5" data-id="element-1546">
              {/* Stacked bar */}
              <div className="flex h-2.5 rounded-full overflow-hidden bg-stone-100" data-id="element-1547">
                {statusDistribution.items.filter(item => item.count > 0).map((item, index) => <motion.div key={item.key} initial={{
              width: 0
            }} animate={{
              width: `${item.percentage}%`
            }} transition={{
              duration: 0.6,
              delay: index * 0.05,
              ease: 'easeOut'
            }} className={`${item.color} border-r border-white last:border-r-0`} title={`${item.label}: ${item.count}`} data-id="element-1548" />)}
              </div>

              {/* Inline Legend */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3" data-id="element-1549">
                {statusDistribution.items.map(item => <div key={item.key} className="flex items-center gap-2" data-id="element-1550">
                    <div className={`w-2 h-2 rounded-full ${item.color}`} data-id="element-1551" />
                    <span className="text-sm font-medium text-stone-700" data-id="element-1552">
                      {item.label}
                    </span>
                    <span className="text-sm text-stone-400" data-id="element-1553">{item.count}</span>
                  </div>)}
              </div>
            </div>}
        </div>
      </div>

      {/* Statistics Section */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-id="element-1554">
        <div className="p-5 border-b border-stone-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1555">
          <div className="flex items-center gap-2" data-id="element-1556">
            <TrendingUp className="w-4 h-4 text-stone-400" data-id="element-1557" />
            <h2 className="text-sm font-semibold text-stone-900 uppercase tracking-wide" data-id="element-1558">
              Statistik
            </h2>
          </div>

          <div className="flex p-1 bg-stone-50 rounded-lg border border-stone-200" data-id="element-1559">
            {(['Samlet', 'Ladeboks', 'Andet'] as const).map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${selectedCategory === category ? 'bg-white text-stone-900 shadow-sm border border-stone-200' : 'text-stone-500 hover:text-stone-700 border border-transparent'}`} data-id="element-1560">
                {category === 'Andet' ? 'Alm. Installation' : category}
              </button>)}
          </div>
        </div>

        <div className="p-6 space-y-8" data-id="element-1561">
          {/* Revenue + Hours */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1562">
            {/* Revenue */}
            <div className="p-5 rounded-xl bg-white border border-stone-200 flex flex-col" data-id="element-1563">
              <div className="w-fit p-2 bg-emerald-50 rounded-lg mb-3" data-id="element-1564">
                <TrendingUp className="w-4 h-4 text-emerald-600" data-id="element-1565" />
              </div>
              <span className="text-sm font-medium text-stone-500" data-id="element-1566">
                Omsætning
              </span>
              <p className="text-3xl font-bold tracking-tight text-stone-900 mt-1" data-id="element-1567">
                {statistics.revenue.toLocaleString('da-DK')} kr
              </p>
            </div>

            {/* Hours */}
            <div className="p-5 rounded-xl bg-white border border-stone-200 flex flex-col" data-id="element-1568">
              <div className="w-fit p-2 bg-amber-50 rounded-lg mb-3" data-id="element-1569">
                <Clock className="w-4 h-4 text-amber-600" data-id="element-1570" />
              </div>
              <span className="text-sm font-medium text-stone-500" data-id="element-1571">Timer</span>
              <p className="text-3xl font-bold tracking-tight text-stone-900 mt-1" data-id="element-1572">
                {statistics.hours.toLocaleString('da-DK')} t
              </p>
            </div>
          </div>

          {/* Top Tasks by Revenue */}
          <div data-id="element-1573">
            <div className="flex items-center gap-2 mb-4" data-id="element-1574">
              <Trophy className="w-4 h-4 text-stone-400" data-id="element-1575" />
              <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wide" data-id="element-1576">
                Top opgaver efter omsætning
              </h3>
            </div>

            {statistics.topTasks.length === 0 ? <div className="p-8 flex flex-col items-center justify-center text-center border border-stone-200 rounded-xl border-dashed" data-id="element-1577">
                <Trophy className="w-8 h-8 text-stone-200 mb-3" data-id="element-1578" />
                <p className="text-sm font-medium text-stone-900" data-id="element-1579">
                  Ingen opgaver med omsætning endnu
                </p>
                <p className="text-xs text-stone-500 mt-1" data-id="element-1580">
                  Tilføj priser til dine opgaver for at se dem her.
                </p>
              </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1581">
                {statistics.topTasks.map((task, index) => <motion.button key={task.id} onClick={() => onEditTask(task)} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05
            }} className="group flex items-center gap-4 p-4 rounded-xl bg-white border border-stone-200 hover:border-teal-300 hover:shadow-sm transition-all text-left" data-id="element-1582">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-lg font-bold flex-shrink-0 ${index === 0 ? 'bg-teal-50 text-teal-700' : 'bg-stone-100 text-stone-600'}`} data-id="element-1583">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1584">
                      <p className="text-sm font-semibold text-stone-900 truncate" data-id="element-1585">
                        {task.title}
                      </p>
                      <p className="text-xs text-stone-500 truncate mt-0.5" data-id="element-1586">
                        {task.customerName || 'Ingen kunde'}
                        {task.taskId && ` • ${task.taskId}`}
                      </p>
                      <p className="text-sm font-bold text-stone-900 mt-1" data-id="element-1587">
                        {(Number(task.totalPrice) || 0).toLocaleString('da-DK')}{' '}
                        kr
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-teal-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" data-id="element-1588" />
                  </motion.button>)}
              </div>}
          </div>
        </div>
      </div>
    </div>;
}